<script setup lang="ts">
/**
 * The chat with no conversation open (MAR-948, out of `ChatPanel.vue`): the greeting, the composer
 * (the default slot — `ChatPanel` owns it, since the same composer serves an open thread), and the
 * hints row (MAR-1149 removed the inline recent-chat pills and recent activity from `/app` home).
 */
import { X } from 'lucide-vue-next'
import MarcusMark from '~/components/MarcusMark.vue'
// Explicit: Nuxt's directory-scoped auto-import would register this as `<OnboardingHintsRow>`, and a
// tag that does not resolve fails silently — nothing mounts and nothing warns loudly.
import HintsRow from '~/components/onboarding/HintsRow.vue'
import { pickWelcomeGreeting } from '~/utils/welcomeGreeting'

const props = defineProps<{ compact: boolean }>()

const { t, locale } = useI18n()
const { toggleChatPanel } = usePanelLayout()
const { displayName, profile, organizationMainJurisdiction, updateProfile } = useUserProfile()

const firstName = computed(() => displayName.value?.split(' ')[0] ?? '')
const greetingSeed = ref(0)
onMounted(() => {
  greetingSeed.value = Math.random()
})

const greeting = computed(() =>
  pickWelcomeGreeting(
    {
      locale: locale.value,
      secondPerson: profile.value?.second_person,
      jurisdiction: organizationMainJurisdiction.value,
      firstName: firstName.value,
    },
    greetingSeed.value,
  ),
)

// The hints row (#81, reworked by MAR-933): plan-aware "Prueba: …" chips under the composer, shown
// while the STORED `show_tips` flag is true. The × stamps the profile, so it stays gone everywhere.
const showHints = computed(() => !props.compact && profile.value?.show_tips === true)

async function dismissTips() {
  try {
    await updateProfile({ dismiss_tips: true })
  } catch {
    // The profile is the source of truth and the write failed, so the row stays — better than hiding
    // it locally and having it reappear on the next load with no explanation.
  }
}
</script>

<template>
  <!-- Floating only: a band with the close button, which is also the drag handle (`useChatDock`). -->
  <div
    v-if="props.compact"
    data-chat-drag-handle
    class="px-3 py-1.5 shrink-0 flex items-center justify-between sm:cursor-grab sm:active:cursor-grabbing"
  >
    <span class="text-sm font-medium">{{ t('nav.chat') }}</span>
    <button
      type="button"
      class="hidden sm:inline-flex p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
      :title="t('common.close')"
      @click="toggleChatPanel"
    >
      <X class="h-3.5 w-3.5" />
    </button>
  </div>

  <div class="flex-1 overflow-y-auto flex flex-col">
    <div
      class="@container/welcome flex-1 flex flex-col items-center px-4 py-6 sm:px-6 sm:py-8"
      :class="props.compact ? 'justify-start pt-[30%]' : 'justify-center'"
    >
      <!-- ONE centred column: greeting, composer, and hints (#81, MAR-1149).
           Never reintroduce a SINGLE side column (MAR-449): the composer then centres itself in the
           LEFTOVER width and slides when the side column's fetch resolves. The spacing between rows is
           the wrapper's own gap, which is why nothing here carries a bottom margin. -->
      <div class="w-full flex flex-col gap-6 sm:gap-8" :class="props.compact ? 'max-w-full' : 'max-w-[900px]'">
        <h2
          class="font-brand font-light text-balance text-center flex items-center justify-center gap-2 sm:gap-2.5"
          :class="props.compact ? 'text-2xl' : 'text-2xl sm:text-4xl'"
        >
          <MarcusMark
            class="shrink-0 text-primary w-auto"
            :class="props.compact ? 'h-6' : 'h-7 sm:h-9'"
          />
          <span>{{ greeting }}</span>
        </h2>
        <div class="min-w-0 w-full flex flex-col items-center">
          <slot />

          <HintsRow v-if="showHints" class="w-full max-w-[900px] mt-4" @dismiss="dismissTips" />
        </div>
      </div>
    </div>
  </div>
</template>
